import React, { useState, useEffect, useRef } from "react";
import { getBookingByCancelToken } from "../../actions/bookingAction";
import { cancelEvent } from "../../actions/admin/scheduled-event-actions";
import Logo from "../../assets/images/logo-new.svg";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import { notification } from "../../utils/utility";
import isEmpty from "../../validation/is-empty";
import moment from "moment";
import "./booking-pages.scss";

/* ── Inline icon components ──────────────────────────────────────── */
const DurationIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const TimezoneIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);
/* Larger version for the confirmation icon circle (56x56 container) */
const XCircleIconLg = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);
/* Smaller version for inside buttons (16px) */
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ── Helper: format booking date/time ────────────────────────────── */
const formatBookingTime = (data) => {
  if (!data) return "";
  const start = moment(data.start_time, "HH:mm").format("h:mm A");
  const end   = moment(data.end_time,   "HH:mm").format("h:mm A");
  const day   = moment(data.when,       "DD-MM-YYYY").format("dddd, MMMM D, YYYY");
  return `${start} – ${end} · ${day}`;
};

/* ── Component ───────────────────────────────────────────────────── */
const CancelBooking = (props) => {
  const { token, time } = useParams();

  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [data, setData] = useState(null);
  const cancelReasonRef = useRef();

  useEffect(() => {
    const { getBookingByCancelToken } = props;
    setPageLoading(true);
    getBookingByCancelToken({
      data: { token },
      onSuccess: (response) => {
        setData(response);
        setPageLoading(false);
      },
      onError: () => {
        notification.error("Invalid token, or token is expired.");
        setPageLoading(false);
      },
    });
  }, []);

  const onCancelBooking = () => {
    const cancelReason = cancelReasonRef.current?.value || "";
    const { cancelEvent } = props;
    setPageLoading(true);
    cancelEvent({
      data: {
        booking_id:          data.id,
        cancellation_reason: cancelReason,
        booking_status:      !isEmpty(time) ? "CC" : "CS",
        is_invitee:          !isEmpty(time) ? "E"  : "D",
      },
      onSuccess: () => {
        notification.success("Booking cancelled successfully.");
        setPage(2);
        setPageLoading(false);
      },
      onError: () => {
        notification.error("An error occurred during cancellation. Please try later.");
        setPageLoading(false);
      },
    });
  };

  return (
    <>
      <Loader isShowLoader={pageLoading} />

      <div className="bp-page">
        <div className="bp-card">

          {/* ── Logo ─────────────────────────────────────────────── */}
          <div className="bp-logo-bar">
            <img src={Logo} alt="MeetOcto" />
          </div>

          {/* ══════════════ PAGE 1 — Cancel form ══════════════════ */}
          {page === 1 && data && (
            <>
              {/* Booking summary */}
              <div className="bp-booking-info">
                <p className="bp-invitee">{data.invitee_name}</p>
                <h2 className="bp-event-name">{data.event_name}</h2>

                <div className="bp-details">
                  {data.event_duration && (
                    <div className="bp-detail-row">
                      <DurationIcon />
                      <span>{data.event_duration} minutes</span>
                    </div>
                  )}
                  <div className="bp-detail-row">
                    <CalendarIcon />
                    <span>{formatBookingTime(data)}</span>
                  </div>
                  {data.invitee_timezone && (
                    <div className="bp-detail-row">
                      <TimezoneIcon />
                      <span>{data.invitee_timezone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Cancel form — only shown when booking data is loaded */}
              <div className="bp-body">
                <h3 className="bp-section-title">Cancel Event</h3>
                <p className="bp-section-sub">
                  Are you sure you want to cancel? This action cannot be undone.
                </p>

                <div className="bp-form-group">
                  <label htmlFor="cancelReason">
                    Reason for canceling
                    <span className="bp-optional"> (optional)</span>
                  </label>
                  <textarea
                    ref={cancelReasonRef}
                    id="cancelReason"
                    className="bp-textarea"
                    placeholder="Let the host know why you're canceling…"
                  />
                </div>

                <button className="bp-btn-danger" onClick={onCancelBooking} disabled={pageLoading}>
                  <XIcon />
                  Cancel Event
                </button>
              </div>
            </>
          )}

          {/* ══════════════ PAGE 2 — Confirmed ════════════════════ */}
          {page === 2 && data && (
            <>
              <div className="bp-confirmed">
                <div className="bp-confirmed-icon">
                  <XCircleIconLg />
                </div>
                <h2 className="bp-confirmed-title">Cancellation Confirmed</h2>
                <p className="bp-confirmed-sub">
                  Your meeting with <strong>{data.host}</strong> has been cancelled.
                </p>

                <div className="bp-cancelled-chip">
                  <div className="bp-cancelled-row">
                    <CalendarIcon />
                    <span>{data.event_name} · {formatBookingTime(data)}</span>
                  </div>
                  {data.invitee_timezone && (
                    <div className="bp-cancelled-row">
                      <TimezoneIcon />
                      <span>{data.invitee_timezone}</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── Footer ───────────────────────────────────────────── */}
          <div className="bp-footer">
            <span>Powered by</span>
            <img src={Logo} alt="MeetOcto" />
          </div>

        </div>
      </div>
    </>
  );
};

export default connect(null, {
  getBookingByCancelToken,
  cancelEvent,
})(CancelBooking);
